import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useContentRows, useInvalidate } from "@/lib/siteData";
import { Card, Field, Area, SaveBtn, Ok, Err } from "./adminUI";

const SECTIONS = [
    {
        key: "hero",
        label: "Hero (topo do site)",
        fields: [
            { field: "extra.eyebrow", label: "Etiqueta superior" },
            {
                field: "title",
                label: "Título (1 linha por bloco — a última fica colorida)",
                area: true,
            },
            { field: "subtitle", label: "Subtítulo", area: true },
        ],
    },
    {
        key: "passeios",
        label: "Seção de passeios",
        fields: [
            { field: "extra.label", label: "Etiqueta superior" },
            { field: "title", label: "Título (a última palavra fica destacada)" },
        ],
    },
    {
        key: "contato",
        label: "Seção de contato",
        fields: [
            { field: "extra.label", label: "Etiqueta superior" },
            { field: "title", label: "Título" },
            { field: "subtitle", label: "Texto", area: true },
            { field: "extra.note", label: "Nota inferior (seguidores)" },
        ],
    },
    {
        key: "footer",
        label: "Rodapé",
        fields: [{ field: "body", label: "Texto institucional", area: true }],
    },
    ...[
        ["cancelamento", "Página de cancelamento e reembolso"],
        ["privacidade", "Política de privacidade (LGPD)"],
    ].map(([key, label]) => ({
        key,
        label,
        fields: [
            { field: "title", label: "Título" },
            { field: "subtitle", label: "Introdução", area: true },
            { field: "body", label: "Conteúdo (linhas iniciadas com ## viram subtítulos)", area: true, rows: 14 },
        ],
    })),
];

function get(obj, path) {
    return path.split(".").reduce((o, k) => o?.[k], obj);
}

function setPath(obj, path, value) {
    const [head, ...rest] = path.split(".");
    if (!rest.length) return { ...obj, [head]: value };
    return { ...obj, [head]: setPath(obj[head] || {}, rest.join("."), value) };
}

export default function ContentAdmin() {
    const rows = useContentRows();
    const invalidate = useInvalidate();
    const [forms, setForms] = useState({});
    const [savingKey, setSavingKey] = useState("");
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    const sectionForm = (key) => {
        if (forms[key]) return forms[key];
        const row = rows.find((r) => r.key === key);
        return (
            row || {
                key,
                title: "",
                subtitle: "",
                body: "",
                extra: {},
            }
        );
    };

    useEffect(() => {
        const next = {};
        for (const s of SECTIONS) {
            const row = rows.find((r) => r.key === s.key);
            if (row) next[s.key] = { ...row };
        }
        setForms(next);
    }, [rows]);

    const change = (key, path, value) => {
        setForms((f) => ({
            ...f,
            [key]: setPath(f[key] || sectionForm(key), path, value),
        }));
    };

    const save = async (key) => {
        setSavingKey(key);
        setErr("");
        const form = forms[key];
        const payload = {
            key,
            title: form.title || "",
            subtitle: form.subtitle || "",
            body: form.body || "",
            extra: form.extra || {},
            updated_at: new Date().toISOString(),
        };
        const { error } = await supabase
            .from("content_sections")
            .upsert(payload);
        setSavingKey("");
        if (error) setErr("Erro: " + error.message);
        else {
            setMsg("Salvo!");
            invalidate("content");
            setTimeout(() => setMsg(""), 2500);
        }
    };

    return (
        <div className="space-y-8">
            <div>
                <h1 className="font-display text-2xl font-black uppercase sm:text-3xl">
                    Conteúdo do site
                </h1>
                <p className="mt-2 text-sm text-dim">
                    Textos principais do site público.
                </p>
            </div>
            <div className="flex items-center gap-3">
                <Ok msg={msg} />
                <Err msg={err} />
            </div>
            {SECTIONS.map((s) => {
                const form = sectionForm(s.key);
                return (
                    <Card key={s.key} className="space-y-4">
                        <p className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-neon-cyan">
                            {s.label}
                        </p>
                        {s.fields.map((f) =>
                            f.area ? (
                                <Area
                                    key={f.field}
                                    rows={f.rows}
                                    label={f.label}
                                    value={get(form, f.field) ?? ""}
                                    onChange={(v) =>
                                        change(s.key, f.field, v)
                                    }
                                    testid={`content-${s.key}-${f.field.replace(".", "-")}`}
                                />
                            ) : (
                                <Field
                                    key={f.field}
                                    label={f.label}
                                    value={get(form, f.field) ?? ""}
                                    onChange={(v) =>
                                        change(s.key, f.field, v)
                                    }
                                    testid={`content-${s.key}-${f.field.replace(".", "-")}`}
                                />
                            ),
                        )}
                        <SaveBtn
                            onClick={() => save(s.key)}
                            saving={savingKey === s.key}
                            testid={`content-save-${s.key}`}
                        />
                    </Card>
                );
            })}
        </div>
    );
}
